// FILE: src/utils/firebase/features/transactions/writers.js
import { getDbOrThrow } from "../../core/firestoreCore";
import { COLLECTIONS, userScopedId, slugify } from "../../core/collections";
import {
  collection, doc, getDoc, getDocs, setDoc, writeBatch,
  serverTimestamp, increment, query, where,
} from "firebase/firestore";
import { isDryRunFlag, logDryRunBanner } from "../../core/writerUtils";
import {
  shapeTagForWrite, nodesFromTree, normalizeFullPath,
  segmentsFromPath, TAG_PATH_SEP,
} from "../../../../components/app/bases/UploadDataComponent/utils/tagUtils";

import {
  patchTxCacheAfterAdd, patchTxCacheAfterUpdate, patchTxCacheAfterDelete,
  patchTxCacheAfterBulkAdd, clearAllTxCachesForUser, // kept import for compatibility; no longer used here
} from "../../cache/transactionsCached";

import { applyAccountBalanceDeltas } from "../accounts/writers";

/* ----------------- utils ----------------- */
function pruneUndefinedDeep(value) {
  if (Array.isArray(value)) return value.map(pruneUndefinedDeep);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) if (v !== undefined) out[k] = pruneUndefinedDeep(v);
    return out;
  }
  return value;
}

function parseAmountValueLoose(v) {
  if (typeof v === "number") return v;
  if (v == null) return NaN;
  let s = String(v).trim(); let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[^\d.,-]+/g, "");
  if (s.includes(",") && s.includes(".")) s = s.replace(/,/g, "");
  else if (s.includes(",") && !s.includes(".")) s = s.replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? NaN : (neg ? -n : n);
}

/* tag normalization */
function normalizeTagFields(tx) {
  const src = tx && typeof tx === "object" ? { ...tx } : {};
  const toArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      return val.split(/[|;,]/g).map((s) => normalizeFullPath(String(s || "").trim())).filter(Boolean);
    }
    return [];
  };
  const a = toArray(src.tagPath);
  const b = toArray(src.tagPaths);
  const merged = Array.from(new Set([...a, ...b].map((s) => normalizeFullPath(String(s || ""))))).filter(Boolean);
  if (merged.length) src.tagPath = merged; else delete src.tagPath;
  delete src.tagPaths;
  return src;
}

function deriveSearchFields(tx) {
  const out = {};
  if (typeof tx?.description === "string") out.descriptionLower = tx.description.trim().toLowerCase();
  return out;
}

export function shapeTransaction(tx) {
  const src = normalizeTagFields(tx);
  if (typeof src.amount === "string") {
    const n = parseAmountValueLoose(src.amount);
    if (Number.isFinite(n)) src.amount = n;
  }
  if (typeof src.date === "string") src.date = src.date.trim();
  Object.assign(src, deriveSearchFields(src));
  return pruneUndefinedDeep(src);
}

/* -------- accountId scoping for balance deltas -------- */
function toScopedAccountId(user, rawIdOrSlug) {
  const raw = String(rawIdOrSlug || "").trim();
  if (!raw) return "";
  if (raw.includes(":")) return raw;
  return user?.uid ? userScopedId(user.uid, slugify(raw)) : raw;
}

function deltasForTx(tx) {
  const out = [];
  const accountId = String(tx?.accountId || "").trim();
  if (!accountId) return out;
  const n = parseAmountValueLoose(tx?.amount);
  if (!Number.isFinite(n) || n === 0) return out;

  const type = String(tx?.typeName || tx?.type || "").toLowerCase();
  const side = String(tx?.transferSide || "").toUpperCase();
  const abs = Math.abs(n);
  let delta;

  if (type.includes("transfer")) {
    if (side === "OUT") delta = -abs;
    else if (side === "IN") delta = +abs;
    else delta = n;
  } else if (type.includes("expense") || type.includes("debit")) {
    delta = -abs;
  } else if (type.includes("income") || type.includes("credit") || type.includes("refund")) {
    delta = +abs;
  } else if (type.includes("adjustment")) {
    delta = n;
  } else {
    delta = n;
  }

  if (delta && delta !== 0) out.push({ accountId, delta });
  return out;
}

function deltasForTxScoped(user, tx) {
  const base = deltasForTx(tx);
  const out = [];
  for (const d of base) {
    const scoped = toScopedAccountId(user, d.accountId);
    if (scoped) out.push({ accountId: scoped, delta: d.delta });
  }
  return out;
}

function mergeDeltas(list) {
  const m = new Map();
  for (const { accountId, delta } of list) {
    if (!accountId || !Number.isFinite(delta)) continue;
    m.set(accountId, (m.get(accountId) || 0) + delta);
  }
  return m;
}

/* -------- config/meta -------- */
function _userTxConfigRef(db, uid) {
  const id = userScopedId(uid, "transactions-table");
  return doc(collection(db, COLLECTIONS.userConfigTransactions), id);
}
export async function readUserTransactionsConfig({ user }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const snap = await getDoc(_userTxConfigRef(db, user.uid));
  if (!snap.exists()) return { exists: false };
  return { exists: true, data: snap.data() || {} };
}
export async function initUserTransactionsConfigIfMissing({ user, allColumns = [] }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const ref = _userTxConfigRef(db, user.uid);
  const snap = await getDoc(ref);
  const sanitizedAll = Array.from(new Set(allColumns));
  if (!snap.exists()) {
    const payload = {
      userId: user.uid,
      userEmail: (user.email || "").toLowerCase(),
      kind: "transactions_table",
      version: 1,
      allColumns: sanitizedAll,
      selectedColumns: sanitizedAll,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(ref, payload, { merge: true });
    return { exists: true, data: payload };
  }
  const data = snap.data() || {};
  const currentAll = Array.isArray(data.allColumns) ? data.allColumns : [];
  const mergedAll = Array.from(new Set([...(currentAll || []), ...sanitizedAll]));
  if (mergedAll.length !== currentAll.length) {
    await setDoc(ref, { allColumns: mergedAll, updatedAt: serverTimestamp() }, { merge: true });
    data.allColumns = mergedAll;
  }
  return { exists: true, data };
}
export async function updateUserTransactionsSelectedColumns({ user, selectedColumns = [] }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const ref = _userTxConfigRef(db, user.uid);
  const payload = { selectedColumns: Array.from(new Set(selectedColumns)), updatedAt: serverTimestamp() };
  await setDoc(ref, payload, { merge: true });
  return payload;
}

function _userTxMetaRef(db, userIdOrEmail) {
  return doc(collection(db, COLLECTIONS.userTransactionsMeta), String(userIdOrEmail).toLowerCase());
}
async function bumpUserTransactionsMeta({ user }) {
  const db = getDbOrThrow();
  const metaRef = _userTxMetaRef(db, user.email || user.uid);
  await setDoc(metaRef, { version: increment(1), lastWriteAt: serverTimestamp() }, { merge: true });
}

/* -------- tags ensure -------- */
async function ensureTagPathsExistForUser({ user, tagPaths = [], source = "tx-ensure-tag" }) {
  const db = getDbOrThrow();
  const uid = user?.uid;
  const email = String(user?.email || "").toLowerCase();
  if (!uid || !tagPaths?.length) return;

  const prefixMap = new Map();
  for (const raw of tagPaths) {
    const full = normalizeFullPath(raw || ""); if (!full) continue;
    const segs = segmentsFromPath(full); const prefixes = [];
    for (let i = 0; i < segs.length; i++) {
      prefixes.push(segs[i]);
      const fp = prefixes.join(TAG_PATH_SEP);
      const shaped = shapeTagForWrite({ fullPath: fp, name: segs[i] });
      if (shaped?.slug && !prefixMap.has(shaped.slug)) prefixMap.set(shaped.slug, shaped);
    }
  }
  if (prefixMap.size === 0) return;

  const batch = writeBatch(db);
  let writes = 0;
  for (const shaped of prefixMap.values()) {
    const id = userScopedId(uid, shaped.slug);
    const ref = doc(collection(db, COLLECTIONS.userTags), id);
    const snap = await getDoc(ref);
    const base = {
      name: shaped.name, fullPath: shaped.fullPath, slug: shaped.slug,
      parentPath: shaped.parentPath, parentSlug: shaped.parentSlug,
      ancestors: shaped.ancestors, depth: shaped.depth,
      userId: uid, userEmail: email, source,
      ...(snap.exists() ? {} : { createdAt: serverTimestamp() }),
      updatedAt: serverTimestamp(),
    };
    batch.set(ref, pruneUndefinedDeep(base), { merge: true });
    writes++;
    if (writes % 400 === 0) { await batch.commit(); writes = 0; }
  }
  if (writes > 0) await batch.commit();
}

/* -------- change log helpers -------- */
function _queueTxChange({ db, email, batch, txId, op, groupId, now }) {
  const cref = doc(collection(db, COLLECTIONS.userTransactionChanges));
  batch.set(cref, pruneUndefinedDeep({
    userEmail: String(email || "").toLowerCase(),
    txId: String(txId), transferGroupId: groupId || null, op,
    changedAt: now, updatedAt: now,
  }), { merge: true });
}

/* -------- transfer helpers -------- */
async function _getTransferLegs({ userEmail, transferGroupId }) {
  const db = getDbOrThrow();
  const qRef = query(
    collection(db, COLLECTIONS.userTransactions),
    where("userEmail", "==", String(userEmail).toLowerCase()),
    where("transferGroupId", "==", String(transferGroupId))
  );
  const snap = await getDocs(qRef);
  const legs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const out = legs.find(x => String(x?.transferSide || "").toUpperCase() === "OUT") || null;
  const inn = legs.find(x => String(x?.transferSide || "").toUpperCase() === "IN") || null;
  return { out, inn };
}

function _shapeTransferLegs({
  user, fromAccountId, toAccountId, amountAbs, currencyCode, date,
  description, tagPath = [], source = "manual", groupId, outId, inId,
}) {
  const abs = Math.abs(parseAmountValueLoose(amountAbs));
  if (!Number.isFinite(abs) || abs <= 0) throw new Error("amountAbs must be > 0");
  if (!fromAccountId || !toAccountId) throw new Error("Both fromAccountId and toAccountId are required.");
  const shapedCommon = pruneUndefinedDeep({
    userId: user.uid,
    userEmail: String(user.email || "").toLowerCase(),
    date: String(date || "").trim(),
    currencyCode: String(currencyCode || "").trim(),
    description: description != null ? String(description) : undefined,
    tagPath: normalizeTagFields({ tagPath }).tagPath,
    typeName: "transfer",
    transferGroupId: groupId,
    source,
    ...deriveSearchFields({ description }),
  });
  const outLeg = pruneUndefinedDeep({ ...shapedCommon, id: outId, accountId: fromAccountId, transferSide: "OUT", amount: -abs });
  const inLeg  = pruneUndefinedDeep({ ...shapedCommon, id: inId,  accountId: toAccountId, transferSide: "IN",  amount: +abs });
  return { outLeg, inLeg };
}

function _netDeltasForTransfer({ user, prevOut, prevIn, nextOut, nextIn }) {
  const prevList = [];
  if (prevOut) prevList.push(...deltasForTxScoped(user, prevOut));
  if (prevIn)  prevList.push(...deltasForTxScoped(user, prevIn));
  const nextList = [];
  if (nextOut) nextList.push(...deltasForTxScoped(user, nextOut));
  if (nextIn)  nextList.push(...deltasForTxScoped(user, nextIn));
  const prevMap = mergeDeltas(prevList);
  const nextMap = mergeDeltas(nextList);
  const net = new Map(nextMap);
  for (const [aid, dPrev] of prevMap.entries()) net.set(aid, (net.get(aid) || 0) - dPrev);
  return net;
}

/* -------- CSV bulk writer (no change log to avoid write limits) -------- */
export async function writeProcessedData({ processed, accountMapping = {}, user, status = () => {}, dryRun }) {
  if (!processed) throw new Error("Nothing to write.");
  if (!user?.uid) throw new Error("Not signed in.");

  const db = getDbOrThrow();
  const uid = user.uid;
  const email = (user.email || "").toLowerCase();
  const DRY = isDryRunFlag(dryRun);

  if (DRY) {
    status("DRY RUN enabled — payloads prepared, no writes performed.");
    logDryRunBanner("writeProcessedData()", {
      accounts: (processed.accounts || []).length,
      tagNodes: (processed.tags?.nodes || []).length,
      tagRoots: (processed.tags?.tree || []).length,
      transactions: (processed.transactions || []).length,
    });
  }

  // A) accounts — overwrite CSV balances
  {
    const accountsList = processed.accounts || [];
    if (!DRY && accountsList.length) {
      const batch = writeBatch(db);
      for (const acc of accountsList) {
        const idPart = String(acc.slug || slugify(acc.name || "") || "").toLowerCase();
        const id = userScopedId(uid, idPart);
        const ref = doc(collection(db, COLLECTIONS.userAccounts), id);

        const mapped = accountMapping[acc.slug || ""];
        const mappedSpecials =
          Array.isArray(mapped?.special_attributes) ? { special_attributes: mapped.special_attributes } :
          Array.isArray(mapped?.specialAttributes) ? { specialAttributes: mapped.specialAttributes } : {};

        const csvBalanceRaw =
          acc.accountBalance !== undefined ? acc.accountBalance :
          acc.balance !== undefined ? acc.balance : 0;

        const csvBalance = parseAmountValueLoose(csvBalanceRaw);
        const nextBalance = Number.isFinite(csvBalance) ? csvBalance : 0;

        const payloadRaw = {
          ...acc,
          slug: acc.slug || idPart,
          accountBalance: nextBalance,
          ...(mapped ? {
            systemTypeId: mapped.id,
            systemTypeName: mapped.name,
            systemTypeDesc: mapped.desc,
          } : {}),
          ...((acc.specialAttributes === undefined && acc.special_attributes === undefined) ? mappedSpecials : {}),
          userId: uid,
          userEmail: email,
          source: acc.source || "csv-upload",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        batch.set(ref, pruneUndefinedDeep(payloadRaw), { merge: true });
      }
      await batch.commit();
    }
  }

  // B) tags
  {
    const rawTree = processed?.tags?.tree || [];
    let sourceNodes;
    if (Array.isArray(rawTree) && rawTree.length > 0) sourceNodes = nodesFromTree(rawTree);
    else {
      const flatNodes = processed?.tags?.nodes || [];
      sourceNodes = flatNodes.map((n) => {
        const fullPath = normalizeFullPath(n?.fullPath || n?.name || "");
        return { name: (n?.name || "").trim() || fullPath, fullPath, slug: (n?.slug || slugify(fullPath)).toLowerCase() };
      });
    }

    const shaped = (sourceNodes || []).map(shapeTagForWrite).filter(Boolean);
    if (!DRY && shaped.length) {
      const batch = writeBatch(db);
      for (const t of shaped) {
        const idPart = (t.slug || slugify(t.fullPath || t.name || "")).toLowerCase();
        const id = userScopedId(uid, idPart);
        const ref = doc(collection(db, COLLECTIONS.userTags), id);
        const payloadRaw = {
          name: t.name, fullPath: t.fullPath, slug: t.slug,
          parentPath: t.parentPath, parentSlug: t.parentSlug,
          ancestors: t.ancestors, depth: t.depth,
          userId: uid, userEmail: email, source: t.source || "csv-upload",
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        };
        batch.set(ref, pruneUndefinedDeep(payloadRaw), { merge: true });
      }
      await batch.commit();
    }
  }

  // C) transactions (no balance or change-log here)
  if (!DRY) {
    const BATCH = 400;
    const list = processed.transactions || [];
    for (let i = 0; i < list.length; i += BATCH) {
      const batch = writeBatch(db);
      const slice = list.slice(i, i + BATCH);
      const now = serverTimestamp();
      const payloads = [];

      for (const rawTx of slice) {
        const tx = normalizeTagFields(rawTx);
        const isTransfer = String(tx?.typeName || "").toLowerCase() === "transfer";
        const hasLegSide = typeof tx?.transferSide === "string" && tx.transferSide.length > 0;
        const hasEndpoints = !!tx?.fromAccountId && !!tx?.toAccountId;

        if (isTransfer && !hasLegSide && hasEndpoints) {
          const groupId = doc(collection(db, COLLECTIONS.userTransactions)).id;
          const outRef = doc(collection(db, COLLECTIONS.userTransactions));
          const inRef  = doc(collection(db, COLLECTIONS.userTransactions));
          const abs = Math.abs(parseAmountValueLoose(tx.amountAbs ?? tx.amount));
          const common = {
            userId: user.uid, userEmail: email, transferGroupId: groupId,
            typeName: "transfer", currencyCode: tx.currencyCode,
            date: tx.date, description: tx.description, tagPath: tx.tagPath,
            source: tx.source || "csv-upload",
          };
          const outPayload = pruneUndefinedDeep({
            ...common, ...deriveSearchFields(common), id: outRef.id,
            accountId: tx.fromAccountId, transferSide: "OUT", amount: -abs,
            createdAt: now, updatedAt: now,
          });
          const inPayload = pruneUndefinedDeep({
            ...common, ...deriveSearchFields(common), id: inRef.id,
            accountId: tx.toAccountId, transferSide: "IN", amount: +abs,
            createdAt: now, updatedAt: now,
          });
          batch.set(outRef, outPayload, { merge: true });
          batch.set(inRef,  inPayload,  { merge: true });
          payloads.push(outPayload, inPayload);
        } else {
          const ref = tx.id
            ? doc(collection(db, COLLECTIONS.userTransactions), String(tx.id))
            : doc(collection(db, COLLECTIONS.userTransactions));
          const id = ref.id;
          const payload = pruneUndefinedDeep({
            ...tx, ...deriveSearchFields(tx), id,
            userId: user.uid, userEmail: email,
            source: tx.source || "csv-upload",
            createdAt: now, updatedAt: now,
          });
          batch.set(ref, payload, { merge: true });
          payloads.push(payload);
        }
      }

      await batch.commit();
      try { await patchTxCacheAfterBulkAdd({ userEmail: email, txs: payloads }); } catch {}
    }
  }

  try { if (!DRY) await clearAllTxCachesForUser(email); } catch {} // no-op safety; okay to keep
  return {
    accounts: (processed.accounts || []).length,
    tags:     (processed.tags?.nodes || []).length,
    transactions: (processed.transactions || []).length,
    dryRun: DRY,
  };
}

/* -------- Manual create / update / delete -------- */
export async function createManualTransaction({ user, tx }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();
  const shapedTx = normalizeTagFields(tx);

  // consolidated transfer
  const isTransfer = String(shapedTx?.typeName || "").toLowerCase() === "transfer";
  const hasEndpoints = !!shapedTx?.fromAccountId && !!shapedTx?.toAccountId;
  if (isTransfer && hasEndpoints) {
    return await createTransfer({
      user,
      fromAccountId: shapedTx.fromAccountId,
      toAccountId: shapedTx.toAccountId,
      amountAbs: shapedTx.amountAbs != null ? shapedTx.amountAbs : Math.abs(parseAmountValueLoose(shapedTx.amount)),
      currencyCode: shapedTx.currencyCode,
      date: shapedTx.date,
      description: shapedTx.description,
      tagPath: shapedTx.tagPath,
    });
  }

  if (Array.isArray(shapedTx?.tagPath) && shapedTx.tagPath.length) {
    await ensureTagPathsExistForUser({ user, tagPaths: shapedTx.tagPath, source: "manual-create" });
  }

  const ref = doc(collection(db, COLLECTIONS.userTransactions));
  const now = serverTimestamp();
  const record = pruneUndefinedDeep({
    ...shapedTx, ...deriveSearchFields(shapedTx),
    id: ref.id, userId: user.uid, userEmail: email,
    source: shapedTx?.source || "manual", createdAt: now, updatedAt: now,
  });

  // balances
  const net = mergeDeltas(deltasForTxScoped(user, record));
  await applyAccountBalanceDeltas({ user, deltas: net });

  const batch = writeBatch(db);
  batch.set(ref, record, { merge: true });
  _queueTxChange({ db, email, batch, txId: ref.id, op: "add", now });
  await batch.commit();

  await patchTxCacheAfterAdd({ userEmail: email, tx: record });
  await bumpUserTransactionsMeta({ user });
  return record;
}

export async function updateManualTransaction({ user, tx }) {
  if (!user?.uid) throw new Error("Not signed in.");
  if (!tx?.id) throw new Error("Missing transaction id.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();

  const shapedTx = normalizeTagFields(tx);
  const ref = doc(db, COLLECTIONS.userTransactions, String(shapedTx.id));
  const prevSnap = await getDoc(ref);
  const prev = prevSnap.exists() ? prevSnap.data() : null;

  const nextType = String(shapedTx?.typeName || prev?.typeName || "").toLowerCase();
  const isTransfer = nextType === "transfer" || !!prev?.transferGroupId || !!shapedTx?.transferGroupId;
  if (isTransfer) {
    const transferGroupId = shapedTx.transferGroupId || prev?.transferGroupId;
    if (!transferGroupId) throw new Error("Missing transferGroupId for transfer update.");

    const patch = {};
    if (Object.prototype.hasOwnProperty.call(shapedTx, "description")) patch.description = shapedTx.description;
    if (Object.prototype.hasOwnProperty.call(shapedTx, "date")) patch.date = shapedTx.date;
    if (Object.prototype.hasOwnProperty.call(shapedTx, "tagPath")) patch.tagPath = shapedTx.tagPath;
    if (Object.prototype.hasOwnProperty.call(shapedTx, "amount")) patch.amountAbs = Math.abs(parseAmountValueLoose(shapedTx.amount));
    if (Object.prototype.hasOwnProperty.call(shapedTx, "accountId") && prev?.transferSide) {
      if (String(prev.transferSide).toUpperCase() === "OUT") patch.fromAccountId = shapedTx.accountId;
      if (String(prev.transferSide).toUpperCase() === "IN") patch.toAccountId = shapedTx.accountId;
    }
    return await updateTransferGroup({ user, transferGroupId, patch });
  }

  if (Array.isArray(shapedTx?.tagPath) && shapedTx.tagPath.length) {
    await ensureTagPathsExistForUser({ user, tagPaths: shapedTx.tagPath, source: "manual-update" });
  }

  const derived = Object.prototype.hasOwnProperty.call(shapedTx, "description") ? deriveSearchFields(shapedTx) : {};
  const patch = pruneUndefinedDeep({
    ...shapedTx, ...derived, userId: user.uid, userEmail: email, updatedAt: serverTimestamp(),
  });

  // net balances
  const nextMap = mergeDeltas(deltasForTxScoped(user, { ...prev, ...patch }));
  const prevMap = mergeDeltas(prev ? deltasForTxScoped(user, prev) : []);
  const net = new Map(nextMap);
  for (const [aid, dPrev] of prevMap.entries()) net.set(aid, (net.get(aid) || 0) - dPrev);
  await applyAccountBalanceDeltas({ user, deltas: net });

  const batch = writeBatch(db);
  batch.set(ref, patch, { merge: true });
  _queueTxChange({ db, email, batch, txId: String(shapedTx.id), op: "update", now: serverTimestamp() });
  await batch.commit();

  await patchTxCacheAfterUpdate({ userEmail: email, tx: { ...patch, id: String(shapedTx.id) } });
  await bumpUserTransactionsMeta({ user });
  return { id: String(shapedTx.id) };
}

export async function deleteTransactions({ user, ids = [] }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();

  const snaps = await Promise.all(
    (ids || []).filter(Boolean).map((id) => getDoc(doc(db, COLLECTIONS.userTransactions, String(id))))
  );

  const inverseList = []; const toDelete = [];
  for (const s of snaps) {
    if (!s?.exists()) continue;
    const data = s.data();
    for (const d of deltasForTxScoped(user, data)) inverseList.push({ accountId: d.accountId, delta: -d.delta });
    toDelete.push(s.id);
  }
  const net = mergeDeltas(inverseList);
  await applyAccountBalanceDeltas({ user, deltas: net });

  const CHUNK = 400;
  for (let i = 0; i < toDelete.length; i += CHUNK) {
    const batch = writeBatch(db);
    const now = serverTimestamp();
    for (const id of toDelete.slice(i, i + CHUNK)) {
      batch.delete(doc(db, COLLECTIONS.userTransactions, String(id)));
      _queueTxChange({ db, email, batch, txId: String(id), op: "delete", now });
    }
    await batch.commit();
  }

  await patchTxCacheAfterDelete({ userEmail: email, ids: toDelete });
  await bumpUserTransactionsMeta({ user });
  return { deleted: toDelete.length };
}

export async function updateUserTransactionsViewOptions({ user, viewOptions = {} }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const ref = _userTxConfigRef(db, user.uid);
  const payload = { viewOptions: pruneUndefinedDeep(viewOptions), updatedAt: serverTimestamp() };
  await setDoc(ref, payload, { merge: true });
  return payload;
}

/* -------- transfer public API -------- */
export async function createTransfer({
  user, fromAccountId, toAccountId, amountAbs, currencyCode, date, description, tagPath = [],
}) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();

  if (Array.isArray(tagPath) && tagPath.length) {
    await ensureTagPathsExistForUser({ user, tagPaths: tagPath, source: "manual-create-transfer" });
  }

  const outRef = doc(collection(db, COLLECTIONS.userTransactions));
  const inRef  = doc(collection(db, COLLECTIONS.userTransactions));
  const groupId = doc(collection(db, COLLECTIONS.userTransactions)).id;

  const now = serverTimestamp();
  const { outLeg, inLeg } = _shapeTransferLegs({
    user, fromAccountId, toAccountId, amountAbs, currencyCode, date, description, tagPath,
    source: "manual", groupId, outId: outRef.id, inId: inRef.id,
  });

  const outPayload = pruneUndefinedDeep({ ...outLeg, createdAt: now, updatedAt: now });
  const inPayload  = pruneUndefinedDeep({ ...inLeg,  createdAt: now, updatedAt: now });

  const net = _netDeltasForTransfer({ user, prevOut: null, prevIn: null, nextOut: outPayload, nextIn: inPayload });
  await applyAccountBalanceDeltas({ user, deltas: net });

  const batch = writeBatch(db);
  batch.set(outRef, outPayload, { merge: true });
  batch.set(inRef,  inPayload,  { merge: true });
  _queueTxChange({ db, email, batch, txId: outRef.id, op: "add", groupId, now });
  _queueTxChange({ db, email, batch, txId: inRef.id,  op: "add", groupId, now });
  await batch.commit();

  await patchTxCacheAfterBulkAdd({ userEmail: email, txs: [outPayload, inPayload] });
  await bumpUserTransactionsMeta({ user });
  return { transferGroupId: groupId, outId: outRef.id, inId: inRef.id };
}

export async function updateTransferGroup({ user, transferGroupId, patch = {} }) {
  if (!user?.uid) throw new Error("Not signed in.");
  if (!transferGroupId) throw new Error("Missing transferGroupId.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();

  const { out: prevOut, inn: prevIn } = await _getTransferLegs({ userEmail: email, transferGroupId });

  const next = { ...patch };
  const nextAbs =
    next.amountAbs != null
      ? Math.abs(parseAmountValueLoose(next.amountAbs))
      : Math.abs(parseAmountValueLoose(prevIn?.amount ?? prevOut?.amount ?? 0));
  if (!Number.isFinite(nextAbs) || nextAbs <= 0) throw new Error("amountAbs must be > 0");

  const fromId = next.fromAccountId || prevOut?.accountId;
  const toId   = next.toAccountId   || prevIn?.accountId;
  if (!fromId || !toId) throw new Error("Both fromAccountId and toAccountId are required.");

  const nextDate = (next.date != null) ? String(next.date) : (prevOut?.date || prevIn?.date || "");
  const nextDesc = (next.description !== undefined) ? String(next.description) : (prevOut?.description ?? prevIn?.description ?? "");
  const nextTags = (next.tagPath !== undefined)
    ? normalizeTagFields({ tagPath: next.tagPath }).tagPath
    : (prevOut?.tagPath || prevIn?.tagPath || []);
  if (Array.isArray(nextTags) && nextTags.length) {
    await ensureTagPathsExistForUser({ user, tagPaths: nextTags, source: "manual-update-transfer" });
  }
  const currency = prevOut?.currencyCode || prevIn?.currencyCode || undefined;
  const now = serverTimestamp();

  const outId = prevOut?.id || doc(collection(db, COLLECTIONS.userTransactions)).id;
  const inId  = prevIn?.id  || doc(collection(db, COLLECTIONS.userTransactions)).id;

  const baseCommon = pruneUndefinedDeep({
    userId: user.uid, userEmail: email, transferGroupId, typeName: "transfer",
    currencyCode: currency, date: nextDate, description: nextDesc, tagPath: nextTags,
    source: prevOut?.source || prevIn?.source || "manual", ...deriveSearchFields({ description: nextDesc }),
  });

  const nextOut = pruneUndefinedDeep({ ...(prevOut || {}), ...baseCommon, id: outId, accountId: fromId, transferSide: "OUT", amount: -Math.abs(nextAbs), updatedAt: now });
  const nextIn  = pruneUndefinedDeep({ ...(prevIn  || {}), ...baseCommon, id: inId,  accountId: toId,   transferSide: "IN",  amount: +Math.abs(nextAbs), updatedAt: now });

  const net = _netDeltasForTransfer({ user, prevOut: prevOut || null, prevIn: prevIn || null, nextOut, nextIn });
  await applyAccountBalanceDeltas({ user, deltas: net });

  const batch = writeBatch(db);
  const outRef = doc(db, COLLECTIONS.userTransactions, String(outId));
  const inRef  = doc(db, COLLECTIONS.userTransactions, String(inId));
  const withTimestamps = (prev, nxt) => (prev ? nxt : { ...nxt, createdAt: now });
  batch.set(outRef, withTimestamps(prevOut, nextOut), { merge: true });
  batch.set(inRef,  withTimestamps(prevIn,  nextIn),  { merge: true });
  _queueTxChange({ db, email, batch, txId: String(outId), op: "update", groupId: transferGroupId, now });
  _queueTxChange({ db, email, batch, txId: String(inId),  op: "update", groupId: transferGroupId, now });
  await batch.commit();

  await patchTxCacheAfterUpdate({ userEmail: email, tx: { ...nextOut, id: String(outId) } });
  await patchTxCacheAfterUpdate({ userEmail: email, tx: { ...nextIn,  id: String(inId) } });
  await bumpUserTransactionsMeta({ user });
  return { transferGroupId, outId: String(outId), inId: String(inId) };
}

export async function deleteTransferGroup({ user, transferGroupId }) {
  if (!user?.uid) throw new Error("Not signed in.");
  if (!transferGroupId) throw new Error("Missing transferGroupId.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();

  const { out, inn } = await _getTransferLegs({ userEmail: email, transferGroupId });
  if (!out && !inn) return { deleted: 0 };

  const inverseList = [];
  if (out) for (const d of deltasForTxScoped(user, out)) inverseList.push({ accountId: d.accountId, delta: -d.delta });
  if (inn) for (const d of deltasForTxScoped(user, inn)) inverseList.push({ accountId: d.accountId, delta: -d.delta });
  const net = mergeDeltas(inverseList);
  await applyAccountBalanceDeltas({ user, deltas: net });

  const batch = writeBatch(db);
  const now = serverTimestamp();
  const ids = [];
  if (out) { batch.delete(doc(db, COLLECTIONS.userTransactions, String(out.id))); ids.push(String(out.id)); _queueTxChange({ db, email, batch, txId: String(out.id), op: "delete", groupId: transferGroupId, now }); }
  if (inn) { batch.delete(doc(db, COLLECTIONS.userTransactions, String(inn.id))); ids.push(String(inn.id)); _queueTxChange({ db, email, batch, txId: String(inn.id), op: "delete", groupId: transferGroupId, now }); }
  await batch.commit();

  await patchTxCacheAfterDelete({ userEmail: email, ids });
  await bumpUserTransactionsMeta({ user });
  return { deleted: ids.length };
}
